/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Users, UserPlus, Trash2, Mail, ArrowLeft, Edit2, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectUsersPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');   // 'all' | 'mine'
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'DEPARTMENT_HEAD',
    department: 'ART',
    access_level: 'editor'
  });
  const [inviting, setInviting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editDraft, setEditDraft] = useState({ role: '', department: '', access_level: '' });
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newResetPassword, setNewResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const canManageUsers = user?.role === 'ADMIN' || user?.role === 'PRODUCER';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invited ${inviteData.email}`);
      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'DEPARTMENT_HEAD', department: 'ART', access_level: 'editor' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleSaveEdit = async (userId) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/users/${userId}`, editDraft, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member updated');
      setEditingUserId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this user from the project?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleResetPassword = async () => {
    if (!newResetPassword || newResetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post(`${API}/auth/users/${resetPasswordUserId}/admin-reset-password`,
        { new_password: newResetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetPasswordTarget}`);
      setResetPasswordUserId(null);
      setResetPasswordTarget(null);
      setNewResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const renderUserCard = (u) => (
              <Card key={u.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-6" data-testid={`user-card-${u.email}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-[#e8c547]/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#e8c547]" />
                  </div>
                  <div className="flex gap-1">
                    {canManageUsers && u.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingUserId(u.id); setEditDraft({ role: u.project_role || u.role, department: u.project_department || u.department, access_level: u.access_level || 'editor' }); }}
                        className="text-[#a0a0a0] hover:text-[#e8c547]"
                        data-testid={`edit-user-${u.email}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                    {canManageUsers && u.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setResetPasswordUserId(u.id); setResetPasswordTarget(u.email); setNewResetPassword(''); }}
                        className="text-[#a0a0a0] hover:text-amber-400"
                        title="Reset password"
                        data-testid={`reset-pw-${u.email}`}
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                    )}
                    {canManageUsers && u.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(u.id)}
                        className="text-red-400 hover:bg-red-500/10"
                        data-testid={`remove-user-${u.email}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <h3 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0] mb-1">{u.full_name}</h3>
                <p className="text-[#a0a0a0] text-sm mb-3 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {u.email}
                </p>
                {editingUserId === u.id ? (
                  <div className="space-y-2" data-testid={`edit-user-form-${u.email}`}>
                    <Select value={editDraft.role} onValueChange={v => setEditDraft(p => ({ ...p, role: v }))}>
                      <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                        {['ADMIN','PRODUCER','DIRECTOR','DEPARTMENT_HEAD','CREW','VIEWER'].map(r => (
                          <SelectItem key={r} value={r}>{r.replace('_',' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={editDraft.department} onValueChange={v => setEditDraft(p => ({ ...p, department: v }))}>
                      <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                        {['PRODUCER','DIRECTOR','ART','COSTUME_WARDROBE','MAKEUP_HAIR','VFX','SFX_STUNTS','SOUND','PRODUCTION','OTHER'].map(d => (
                          <SelectItem key={d} value={d}>{d.replace(/_/g,' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={editDraft.access_level} onValueChange={v => setEditDraft(p => ({ ...p, access_level: v }))}>
                      <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-8 text-xs" data-testid={`edit-access-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                        <SelectItem value="viewer">Viewer (read only)</SelectItem>
                        <SelectItem value="commenter">Commenter (read + comment)</SelectItem>
                        <SelectItem value="editor">Editor (full access)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => handleSaveEdit(u.id)} className="bg-[#e8c547] hover:bg-[#d6b539] text-black flex-1 h-8 text-xs" data-testid={`save-user-${u.email}`}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingUserId(null)} className="border-[#2a2a2a] text-[#f0f0f0] h-8 text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-[#e8c547]/20 text-[#e8c547]">{(u.project_role || u.role)?.replace(/_/g,' ')}</Badge>
                    <Badge className="bg-[#4a9eff]/20 text-[#4a9eff]">{(u.project_department || u.department)?.replace(/_/g,' ')}</Badge>
                    <Badge className={
                      u.access_level === 'editor' ? 'bg-[#44bb88]/20 text-[#44bb88]'
                      : u.access_level === 'commenter' ? 'bg-[#e8c547]/20 text-[#e8c547]'
                      : 'bg-[#5a5a5a]/20 text-[#a0a0a0]'
                    } data-testid={`access-badge-${u.email}`}>
                      {u.access_level || 'editor'}
                    </Badge>
                  </div>
                )}
              </Card>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-8" data-testid="project-users-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/projects/${projectId}/breakdown`)}
              className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Breakdown
            </Button>
            <h1 className="font-['DM_Serif_Display'] text-4xl text-[#f0f0f0] mb-2">Teams</h1>
            <p className="text-[#a0a0a0]">Manage everyone with access to this project</p>
          </div>
          {canManageUsers && (
            <Button
              onClick={() => setShowInviteDialog(true)}
              className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
              data-testid="invite-user-btn"
            >
              <UserPlus className="h-5 w-5 mr-2" />
              Invite User
            </Button>
          )}
        </div>

        {/* Tabs: My Department / All Teams */}
        <div className="flex gap-1 mb-6 border-b border-[#2a2a2a]" data-testid="teams-tabs">
          <button
            onClick={() => setTab('mine')}
            data-testid="teams-tab-mine"
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              tab === 'mine'
                ? 'text-[#e8c547] border-[#e8c547]'
                : 'text-[#a0a0a0] border-transparent hover:text-[#f0f0f0]'
            }`}
          >
            My Department
          </button>
          <button
            onClick={() => setTab('all')}
            data-testid="teams-tab-all"
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              tab === 'all'
                ? 'text-[#e8c547] border-[#e8c547]'
                : 'text-[#a0a0a0] border-transparent hover:text-[#f0f0f0]'
            }`}
          >
            All Teams
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#a0a0a0]">Loading users...</div>
        ) : (() => {
          // Filter by tab
          const myDept = user?.department;
          const visible = tab === 'mine'
            ? users.filter(u => (u.project_department || u.department) === myDept)
            : users;

          if (tab === 'mine' && visible.length === 0) {
            return (
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center" data-testid="empty-my-dept">
                <Users className="h-12 w-12 text-[#555] mx-auto mb-3" />
                <p className="text-[#a0a0a0]">No other members in your department yet.</p>
              </Card>
            );
          }

          // Group by department for "All Teams"
          if (tab === 'all') {
            const grouped = {};
            visible.forEach(u => {
              const d = (u.project_department || u.department || 'OTHER');
              if (!grouped[d]) grouped[d] = [];
              grouped[d].push(u);
            });
            const deptOrder = Object.keys(grouped).sort();
            return (
              <div className="space-y-8" data-testid="teams-grouped">
                {deptOrder.map(d => (
                  <div key={d} data-testid={`dept-group-${d}`}>
                    <h2 className="text-sm uppercase font-semibold text-[#a0a0a0] tracking-wider mb-3">
                      {d.replace(/_/g, ' ')}
                      <span className="ml-2 text-[#666]">({grouped[d].length})</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {grouped[d].map(u => renderUserCard(u))}
                    </div>
                  </div>
                ))}
              </div>
            );
          }

          // My Department (flat grid)
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-grid">
              {visible.map(u => renderUserCard(u))}
            </div>
          );
        })()}

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPasswordUserId} onOpenChange={open => { if (!open) { setResetPasswordUserId(null); setResetPasswordTarget(null); setNewResetPassword(''); } }}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Reset Password</DialogTitle>
              <DialogDescription className="text-[#a0a0a0]">
                Set a new password for <span className="text-[#e8c547]">{resetPasswordTarget}</span>. They will need to use this new password to log in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">New Password *</label>
                <Input
                  type="password"
                  value={newResetPassword}
                  onChange={e => setNewResetPassword(e.target.value)}
                  className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                  placeholder="At least 6 characters"
                  data-testid="reset-pw-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !newResetPassword}
                  className="bg-[#e8c547] hover:bg-[#d6b539] text-black flex-1"
                  data-testid="confirm-reset-pw-btn"
                >
                  {resettingPassword ? 'Resetting…' : 'Reset Password'}
                </Button>
                <Button variant="outline" onClick={() => { setResetPasswordUserId(null); setNewResetPassword(''); }}
                  className="border-[#2a2a2a] text-[#f0f0f0]">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Invite Team Member</DialogTitle>
              <DialogDescription className="text-[#a0a0a0]">
                Invite an existing user to this project. They must already have a FiPr.ai account.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Email *</label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                  placeholder="colleague@example.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Role</label>
                <Select value={inviteData.role} onValueChange={(v) => setInviteData({...inviteData, role: v})}>
                  <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                    <SelectItem value="PRODUCTION_MANAGER">Production Manager</SelectItem>
                    <SelectItem value="DEPARTMENT_HEAD">Department Head</SelectItem>
                    <SelectItem value="DEPARTMENT_ASSISTANT">Department Assistant</SelectItem>
                    <SelectItem value="DIRECTOR">Director</SelectItem>
                    <SelectItem value="VIEWER">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Department</label>
                <Select value={inviteData.department} onValueChange={(v) => setInviteData({...inviteData, department: v})}>
                  <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="invite-dept-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                    <SelectItem value="ART">Art</SelectItem>
                    <SelectItem value="COSTUME_WARDROBE">Costume/Wardrobe</SelectItem>
                    <SelectItem value="CASTING">Casting</SelectItem>
                    <SelectItem value="PRODUCTION">Production</SelectItem>
                    <SelectItem value="MAKEUP_HAIR">Makeup/Hair</SelectItem>
                    <SelectItem value="VFX">VFX</SelectItem>
                    <SelectItem value="LOCATIONS">Locations</SelectItem>
                    <SelectItem value="SFX_STUNTS">SFX/Stunts</SelectItem>
                    <SelectItem value="SOUND">Sound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Access Level</label>
                <Select value={inviteData.access_level} onValueChange={(v) => setInviteData({...inviteData, access_level: v})}>
                  <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="invite-access-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                    <SelectItem value="viewer">Viewer (read only)</SelectItem>
                    <SelectItem value="commenter">Commenter (read + comment)</SelectItem>
                    <SelectItem value="editor">Editor (full access in their department)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#a0a0a0] mt-1">Department determines what they can edit; access level controls read/comment/edit power.</p>
              </div>
              <Button
                type="submit"
                disabled={inviting}
                className="w-full bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
                data-testid="submit-invite-btn"
              >
                {inviting ? 'Inviting...' : 'Send Invitation'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ProjectUsersPage;
